import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AuthService } from "@app/auth.service";
import { UiKitModule } from "@app/ui-kit.module";
import { CountrySelectionFormFieldComponent } from "@components/country-selection-form-field/country-selection-form-field.component";
import { LanguageSelectionFormFieldComponent } from "@components/language-selection-form-field/language-selection-form-field.component";
import { ScopeSelectionCardComponent } from "@components/scope-selection-card/scope-selection-card.component";
import { TemplateLocaleService } from "@domain/project/template";
import { Country } from "@domain/user/locale/country";
import { LanguageMap } from "@domain/user/locale/language";
import { LanguageHelper } from "@domain/user/locale/language-helper";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { LogoDirective } from "@odx/angular/components/logo";
import { ToastHelper } from "@utils/toast-helper.service";
import { first } from "rxjs";

@Component({
  selector: "app-scope-selection",
  imports: [
    UiKitModule,
    LanguageSelectionFormFieldComponent,
    ReactiveFormsModule,
    CountrySelectionFormFieldComponent,
    ScopeSelectionCardComponent,
    LogoDirective,
  ],
  providers: [],
  templateUrl: "./scope-selection.component.html",
  styleUrl: "./scope-selection.component.scss",
})
export class ScopeSelectionComponent implements OnInit {
  private templateLocaleService = inject(TemplateLocaleService);
  private languageHelper = inject(LanguageHelper);
  private authService = inject(AuthService);

  protected localeSelectionForm!: FormGroup;

  protected scopeCardsDetails = {
    fgds: {
      scope: BusinessScope.FGDS,
      title: $localize`:@@scopeSelection.fgds.title:Safety`,
      subtitle: $localize`:@@scopeSelection.fgds.subtitle:FGDS`,
      image: "assets/scope-selection-icons/safety_transmitter.svg",
    },
    monitoring: {
      scope: BusinessScope.MONITORING,
      title: $localize`:@@scopeSelection.monitoring.title:Medical`,
      subtitle: $localize`:@@scopeSelection.monitoring.subtitle:Monitoring`,
      image: "assets/scope-selection-icons/medical_monitor.svg",
    },
  };

  protected selectedScope = "";
  protected selectedLanguage = "";
  protected selectedCountry = "";

  protected countryFormFieldLabel = $localize`:@@scopeSelection.countrySelection.lable:Country`;
  protected languageFormFieldLabel = $localize`:@@scopeSelection.languageSelection.lable:Language`;

  protected supportedCountriesBySelectedScope: Country[] = [];
  protected supportedLanguagesBySelectedScope: LanguageMap[] = [];

  private supportedCountriesByScope: Record<BusinessScope, Country[]> = {
    [BusinessScope.FGDS]: [],
    [BusinessScope.MONITORING]: [],
  };
  private supportedLanguagesByScope: Record<BusinessScope, LanguageMap[]> = {
    [BusinessScope.FGDS]: [],
    [BusinessScope.MONITORING]: [],
  };

  private formBuilder = inject(FormBuilder);
  private userService = inject(UserService);
  private toastHelper = inject(ToastHelper);

  private user!: User;

  ngOnInit() {
    this.userService.user$.pipe(first()).subscribe((user) => {
      this.user = user;
    });
    this.localeSelectionForm = this.formBuilder.group({
      language: [null, Validators.required],
      country: [null, Validators.required],
    });

    this.templateLocaleService.getAllScopeLocaleCodes().subscribe({
      next: (locales) => {
        this.supportedCountriesByScope[BusinessScope.FGDS] = locales.fgds.countryCodes.map((code) => ({ countryCode: code }));
        this.supportedCountriesByScope[BusinessScope.MONITORING] = locales.monitoring.countryCodes.map((code) => ({
          countryCode: code,
        }));

        this.supportedLanguagesByScope[BusinessScope.FGDS] = this.languageHelper.buildLanguageMapsFromCodes(
          locales.fgds.languageCodes,
        );
        this.supportedLanguagesByScope[BusinessScope.MONITORING] = this.languageHelper.buildLanguageMapsFromCodes(
          locales.monitoring.languageCodes,
        );
      },
      error: (err: any) => {
        this.toastHelper.error($localize`:@@scopeSelection.error:An error while loading language and country occurred`);
        console.error("Failed loading templates", err);
      },
    });
  }

  onSubmit() {
    if (!this.localeSelectionForm.valid || this.localeSelectionForm.pristine) return;

    this.user.language = this.selectedLanguage;
    this.user.country = this.selectedCountry;
    this.userService.updateUser(this.selectedScope, this.user).subscribe({
      next: () => {
        this.authService.logout();
      },
      error: (err) => {
        this.toastHelper.error($localize`:@@scopeSelection.error:An error while loading language and country occurred`);
        console.error("Unexpected error writing business scope", err);
      },
    });
  }

  setSelectedScope($event: BusinessScope) {
    this.selectedScope = $event;
    this.localeSelectionForm.patchValue({ country: null, language: null });
    this.supportedCountriesBySelectedScope = this.supportedCountriesByScope[$event];
    this.supportedLanguagesBySelectedScope = this.supportedLanguagesByScope[$event];
  }

  setSelectedCountry($event: string) {
    this.selectedCountry = $event;
  }

  setSelectedLanguage($event: string) {
    this.selectedLanguage = $event;
  }
}
